import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, ShieldAlert, Heart, Utensils, Moon } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";
import { useAuth } from "@/context/AuthContext";
import { tzToday } from "@/utils/datetime";

export default function KGChildCard() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { hasPerm, activeMembership } = useAuth();
    const tz = activeMembership?.tenant.timezone || "Asia/Baku";
    const canHealth = hasPerm("kg.health.view");
    const canEditHealth = hasPerm("kg.health.edit");
    const canManageAuth = hasPerm("kg.pickup_manage");
    const canLog = hasPerm("kg.log");

    const [tab, setTab] = useState<"log" | "auths" | "health" | "incidents">("log");
    const [logDay, setLogDay] = useState(tzToday(tz));
    const [log, setLog] = useState<any>({});
    const [auths, setAuths] = useState<any[]>([]);
    const [health, setHealth] = useState<any>(null);
    const [incidents, setIncidents] = useState<any[]>([]);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setErr(null);
        try {
            const [a, i] = await Promise.all([api.get(`/kg/authorizations?child_person_id=${id}`), api.get(`/kg/incidents?child_person_id=${id}`)]);
            setAuths(a.data.items); setIncidents(i.data.items);
            const l = await api.get(`/kg/daily-logs?child_person_id=${id}&day=${logDay}`);
            setLog(l.data.items[0] || { day: logDay, food_status: "", food_notes: "", activity_notes: "", parent_notes: "", history: [] });
            if (canHealth) {
                try { const h = await api.get(`/kg/health/${id}`); setHealth(h.data); } catch { /* 403 ok */ }
            }
        } catch (e: any) { setErr(formatApiError(e)); }
    }, [id, logDay, canHealth]);
    useEffect(() => { load(); }, [load]);

    const saveLog = async () => {
        setBusy(true); setErr(null);
        try {
            await api.put("/kg/daily-logs", { child_person_id: id, day: logDay,
                food_status: log.food_status || null, food_notes: log.food_notes || null,
                activity_notes: log.activity_notes || null, parent_notes: log.parent_notes || null });
            load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    const saveHealth = async () => {
        setBusy(true); setErr(null);
        try { await api.patch(`/kg/health/${id}`, {
            allergies: health.allergies, diet_restrictions: health.diet_restrictions,
            emergency_contact_name: health.emergency_contact_name, emergency_contact_phone: health.emergency_contact_phone,
            medication_notes: health.medication_notes, medication_consent: health.medication_consent,
            medication_instructions: health.medication_instructions,
        }); load(); }
        catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="kg-child-card">
            <div className="flex items-center gap-2">
                <Link to="/kindergarten" className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><ArrowLeft size={14}/> {t("nav.kindergarten")}</Link>
            </div>
            <div className="flex gap-2 border-b" style={{ borderColor: "var(--border)" }}>
                {(["log", "auths", "incidents", ...(canHealth ? ["health"] : [])] as const).map(k => (
                    <button key={k} data-testid={`kgc-tab-${k}`} onClick={() => setTab(k as any)} className="px-4 py-2 text-sm border-b-2" style={{ color: tab === k ? "var(--text)" : "var(--text-muted)", borderColor: tab === k ? "var(--primary)" : "transparent" }}>{t(`kg.tab.${k}`)}</button>
                ))}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

            {tab === "log" && (
                <div className="surface-card p-4 space-y-3" data-testid="log-panel">
                    <div className="flex items-center gap-3">
                        <label className="text-xs" style={{ color: "var(--text-muted)" }}>{t("kg.log.day")}</label>
                        <input data-testid="log-day" type="date" value={logDay} onChange={e => setLogDay(e.target.value)} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                    </div>
                    <div>
                        <div className="text-xs mb-1 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Utensils size={12}/> {t("kg.log.food")}</div>
                        <div className="flex gap-2">
                            {(["ate", "some", "none"] as const).map(v => (
                                <button key={v} data-testid={`food-${v}`} disabled={!canLog} onClick={() => setLog({ ...log, food_status: v })} className="rounded-md border px-3 py-1.5 text-xs" style={{ borderColor: log.food_status === v ? "var(--primary)" : "var(--border)", color: "var(--text)", background: log.food_status === v ? "var(--selected-bg)" : "var(--input)" }}>{t(`kg.food.${v}`)}</button>
                            ))}
                        </div>
                    </div>
                    <input data-testid="log-food-notes" placeholder={t("kg.log.food_notes") as string} disabled={!canLog} value={log.food_notes || ""} onChange={e => setLog({ ...log, food_notes: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" style={{ minHeight: 40 }}/>
                    <div>
                        <div className="text-xs mb-1 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Moon size={12}/> {t("kg.log.activity")}</div>
                        <textarea data-testid="log-activity" disabled={!canLog} value={log.activity_notes || ""} onChange={e => setLog({ ...log, activity_notes: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" rows={2}/>
                    </div>
                    <div>
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("kg.log.parent_notes")}</div>
                        <textarea data-testid="log-parent-notes" disabled={!canLog} value={log.parent_notes || ""} onChange={e => setLog({ ...log, parent_notes: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" rows={2}/>
                    </div>
                    {canLog && <button data-testid="log-save" disabled={busy} onClick={saveLog} className="rounded-md py-2 px-4 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>{busy ? <Loader2 className="animate-spin inline" size={14}/> : t("common.save")}</button>}
                    {log.history && log.history.length > 0 && (
                        <details className="text-xs" data-testid="log-history">
                            <summary style={{ color: "var(--text-muted)" }}>{t("kg.log.history")} ({log.history.length})</summary>
                            <ul className="mt-2 space-y-1">
                                {log.history.map((h: any, idx: number) => (
                                    <li key={idx} className="border-l-2 pl-2" style={{ borderColor: "var(--border)" }}>
                                        <div style={{ color: "var(--text-muted)" }}>{fmtDateTime(h.at)}</div>
                                        <div style={{ color: "var(--text)" }}>{h.food_status || "—"} · {h.food_notes || "—"}</div>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            {tab === "auths" && (
                <div className="surface-card p-4 space-y-2" data-testid="auths-panel">
                    <div className="text-xs inline-flex items-center gap-1 mb-2" style={{ color: "var(--warning-fg)" }}><ShieldAlert size={12}/> {t("kg.auths_hint")}</div>
                    {auths.length === 0 ? <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : auths.map(a => (
                        <div key={a.id} className="rounded-md border p-3 flex items-center justify-between text-sm" style={{ borderColor: "var(--border)" }} data-testid={`auth-${a.id}`}>
                            <div>
                                <div style={{ color: "var(--text)" }}>{a.relation}</div>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{a.valid_from || "—"} → {a.valid_until || "∞"} · {a.is_active ? t("common.active") : t("common.inactive")}</div>
                                {a.notes && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{a.notes}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "health" && canHealth && health && (
                <div className="surface-card p-4 space-y-3" data-testid="health-panel">
                    <div className="text-xs inline-flex items-center gap-1" style={{ color: "var(--warning-fg)" }}><Heart size={12}/> {t("kg.health_hint")}</div>
                    <textarea data-testid="hp-allergies" disabled={!canEditHealth} placeholder={t("kg.health.allergies") as string} value={health.allergies || ""} onChange={e => setHealth({ ...health, allergies: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" rows={2}/>
                    <textarea data-testid="hp-diet" disabled={!canEditHealth} placeholder={t("kg.health.diet") as string} value={health.diet_restrictions || ""} onChange={e => setHealth({ ...health, diet_restrictions: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" rows={2}/>
                    <div className="grid grid-cols-2 gap-2">
                        <input data-testid="hp-em-name" disabled={!canEditHealth} placeholder={t("kg.health.em_name") as string} value={health.emergency_contact_name || ""} onChange={e => setHealth({ ...health, emergency_contact_name: e.target.value })} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                        <input data-testid="hp-em-phone" disabled={!canEditHealth} placeholder={t("kg.health.em_phone") as string} value={health.emergency_contact_phone || ""} onChange={e => setHealth({ ...health, emergency_contact_phone: e.target.value })} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                    </div>
                    <label className="flex items-center gap-2 text-sm" style={{ color: "var(--text)" }}>
                        <input data-testid="hp-consent" type="checkbox" disabled={!canEditHealth} checked={!!health.medication_consent} onChange={e => setHealth({ ...health, medication_consent: e.target.checked })}/>
                        {t("kg.health.medication_consent")}
                    </label>
                    <textarea data-testid="hp-med-notes" disabled={!canEditHealth} placeholder={t("kg.health.medication_notes") as string} value={health.medication_notes || ""} onChange={e => setHealth({ ...health, medication_notes: e.target.value })} className="surface-input px-3 py-2 text-sm w-full" rows={2}/>
                    {canEditHealth && <button data-testid="hp-save" disabled={busy} onClick={saveHealth} className="rounded-md py-2 px-4 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>{busy ? <Loader2 className="animate-spin inline" size={14}/> : t("common.save")}</button>}
                </div>
            )}

            {tab === "incidents" && (
                <div className="surface-card overflow-hidden" data-testid="incidents-panel">
                    {incidents.length === 0 ? <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                            {incidents.map(i => (
                                <li key={i.id} className="p-4 text-sm" data-testid={`incident-${i.id}`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span style={{ color: "var(--text-muted)" }}>{fmtDateTime(i.at)}</span>
                                        <span className={`status-pill ${i.status === "closed" ? "status-success" : "status-warning"}`}>{t(`kg.incident.status.${i.status}`)}</span>
                                    </div>
                                    <div style={{ color: "var(--text)" }}>{i.description}</div>
                                    {i.action_taken && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("kg.incident.action")}: {i.action_taken}</div>}
                                    <div className="text-xs mt-1" style={{ color: i.parent_notified ? "var(--success-fg)" : "var(--warning-fg)" }}>{i.parent_notified ? t("kg.incident.parent_notified") : t("kg.incident.parent_not_notified")}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
